import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Club } from '../../types';

interface ClubState {
  clubs: Club[];
  currentClub: Club | null;
  loading: boolean;
  error: string | null;
}

const initialState: ClubState = {
  clubs: [],
  currentClub: null,
  loading: false,
  error: null,
};

export const fetchClubs = createAsyncThunk('clubs/fetchAll', async () => {
  const response = await api.get<Club[]>('/clubs');
  return response.data;
});

export const fetchClub = createAsyncThunk('clubs/fetchOne', async (clubId: string) => {
  const response = await api.get<Club>(`/club/${clubId}`);
  return response.data;
});

export const createClub = createAsyncThunk('clubs/create', async (clubData: Partial<Club>) => {
  const response = await api.post<Club>('/club', clubData);
  return response.data;
});

export const updateClub = createAsyncThunk(
  'clubs/update',
  async ({ clubId, data }: { clubId: string; data: Partial<Club> }) => {
    const response = await api.put<Club>(`/club/${clubId}`, data);
    return response.data;
  }
);

export const deleteClub = createAsyncThunk('clubs/delete', async (clubId: string) => {
  await api.delete(`/club/${clubId}`);
  return clubId;
});

const clubSlice = createSlice({
  name: 'clubs',
  initialState,
  reducers: {
    setCurrentClub: (state, action: PayloadAction<Club | null>) => {
      state.currentClub = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all clubs
      .addCase(fetchClubs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClubs.fulfilled, (state, action) => {
        state.loading = false;
        state.clubs = action.payload;
      })
      .addCase(fetchClubs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch clubs';
      })
      // Fetch single club
      .addCase(fetchClub.fulfilled, (state, action) => {
        state.currentClub = action.payload;
      })
      // Create club
      .addCase(createClub.fulfilled, (state, action) => {
        state.clubs.push(action.payload);
      })
      // Update club
      .addCase(updateClub.fulfilled, (state, action) => {
        const index = state.clubs.findIndex((c) => c.record_id === action.payload.record_id);
        if (index !== -1) {
          state.clubs[index] = action.payload;
        }
        if (state.currentClub?.record_id === action.payload.record_id) {
          state.currentClub = action.payload;
        }
      })
      // Delete club
      .addCase(deleteClub.fulfilled, (state, action) => {
        state.clubs = state.clubs.filter((c) => c.record_id !== action.payload);
        if (state.currentClub?.record_id === action.payload) {
          state.currentClub = null;
        }
      });
  },
});

export const { setCurrentClub, clearError } = clubSlice.actions;
export default clubSlice.reducer;